import { Link } from "react-router-dom";

const EFFECTIVE = "January 2026";

export default function PrivacyPolicy() {
  return (
    <section className="bg-offwhite py-16 sm:py-24" data-testid="privacy-page">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <p className="text-money font-bold uppercase tracking-widest text-sm mb-3">Aspire Collective Partners LLC</p>
        <h1 className="font-display text-4xl sm:text-5xl text-navy mb-3">Privacy Policy</h1>
        <p className="text-sm text-muted mb-10">Effective {EFFECTIVE}</p>

        <article className="prose-article space-y-8 text-navy-800 text-base leading-relaxed">
          <Section title="1. About this policy">
            <p>
              Aspire Collective Partners LLC (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) buys homes for cash directly from homeowners across the United States. This Privacy Policy explains what information we collect when you submit a lead through this website, how we use that information, who we share it with, and the choices you have. By using this site or submitting a form, you agree to the practices described here.
            </p>
          </Section>

          <Section title="2. Information we collect from you">
            <p>When you complete a lead form on this website (including the homepage &ldquo;Request a Cash Offer&rdquo; form and any situation-specific landing page), we collect:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Your full name</li>
              <li>Your phone number</li>
              <li>Your email address (if provided)</li>
              <li>The property address you are considering selling, including city, state, and ZIP code</li>
              <li>Your estimated property value (if provided)</li>
              <li>The reason you are considering selling and any additional context you share</li>
              <li>Property condition and desired timeline (if you complete the extended form)</li>
              <li>The consent boxes you checked and whether you opted in to marketing text messages</li>
              <li>The page on our site where you submitted the form and a timestamp</li>
            </ul>
            <p>We may also automatically collect technical information such as your IP address, browser type, device, referring page, and interactions with the site.</p>
          </Section>

          <Section title="3. How we use your information">
            <p>We use the information you provide to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Evaluate your property and prepare a no-obligation cash offer;</li>
              <li>Contact you by phone, text message, or email to discuss your inquiry and any offer;</li>
              <li>Coordinate closing, title, and escrow if you accept an offer;</li>
              <li>Send you transactional notifications about your inquiry (for example, confirmation that we received your request);</li>
              <li>Send you marketing text messages or emails <strong>only if you checked the optional marketing consent box</strong>;</li>
              <li>Analyze site performance, improve our services, and produce internal reports (in aggregate or de-identified form);</li>
              <li>Comply with applicable law, respond to legal process, and enforce our terms.</li>
            </ul>
          </Section>

          <Section title="4. Text messages (SMS) and consent">
            <p>
              If you checked the required consent box on our form, you agreed to receive non-marketing text messages from us about your property inquiry and any related transaction. Message frequency varies. Message and data rates may apply.
            </p>
            <p>
              If you also checked the optional consent box, you agreed to receive marketing text messages about promotions, offers, and real-estate opportunities. You can opt out of any of our text messages at any time by replying <strong>STOP</strong>. Reply <strong>HELP</strong> for assistance. Opting out of one message program does not automatically opt you out of the other; if you want to stop both, reply STOP to a message from each program.
            </p>
          </Section>

          <Section title="5. How we share your information">
            <p>We do not sell your personal information. We share it only in the following cases:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Service providers</strong> who help us operate — for example, email delivery (Resend), SMS delivery (Twilio), hosting, analytics, and AI-assisted lead qualification. These providers process your information only under our instructions and are bound by confidentiality obligations.</li>
              <li><strong>Title, escrow, and closing partners</strong> if you accept an offer and we need to complete the transaction.</li>
              <li><strong>Cash buyer partners</strong> where you have expressly asked us to help you find a buyer or where we assign your contract as a wholesaler; when this applies, we share only what is necessary to complete the transaction.</li>
              <li><strong>Legal, safety, and compliance</strong> — when required by law, subpoena, or to protect our rights and the safety of others.</li>
              <li><strong>Business transfers</strong> — if we merge with or are acquired by another company, your information may transfer as part of that transaction.</li>
            </ul>
          </Section>

          <Section title="6. How we protect your information">
            <p>
              We use administrative, technical, and physical safeguards designed to protect the information you share, including encryption in transit (HTTPS), access controls on our admin dashboard, and password-protected accounts. No system is perfectly secure, and we cannot guarantee absolute security. Please tell us right away if you believe your account or information has been compromised.
            </p>
          </Section>

          <Section title="7. How long we keep your information">
            <p>
              We retain lead information for as long as necessary to work your inquiry, complete any resulting transaction, keep required business records, and comply with legal obligations (typically up to 7 years for transaction-related records, and up to 4 years for records of your SMS consent). You can request earlier deletion — see section 8.
            </p>
          </Section>

          <Section title="8. Your choices and rights">
            <p>
              You can (a) request a copy of the personal information we have about you, (b) ask us to correct inaccurate information, (c) ask us to delete your information (subject to legal record-keeping requirements), and (d) opt out of marketing messages at any time. Residents of California, Colorado, Virginia, Connecticut, Utah, and other states with applicable privacy laws have additional rights, including the right to non-discrimination for exercising them.
            </p>
            <p>
              To exercise any of these rights, email us at <a href="mailto:carlos@aspirecollectivepartnersllc.com" className="text-money font-semibold">carlos@aspirecollectivepartnersllc.com</a> or call <a href="tel:+16149664283" className="text-money font-semibold">(614) 966-4283</a>. We may need to verify your identity before we act on your request.
            </p>
          </Section>

          <Section title="9. Cookies and analytics">
            <p>
              We use cookies and similar technologies to keep the site working, remember your preferences, and understand how the site is used. You can control cookies through your browser settings. If we add third-party advertising or retargeting pixels in the future, we will update this policy and, where required, give you an in-page choice before those pixels load.
            </p>
          </Section>

          <Section title="10. Children">
            <p>
              This site is not directed to children under 13, and we do not knowingly collect personal information from them. If you believe a child has provided us information, contact us and we will delete it.
            </p>
          </Section>

          <Section title="11. Changes to this policy">
            <p>
              We may update this Privacy Policy from time to time. When we do, we will update the effective date above and post the new version on this page. Material changes will be highlighted at the top of the page.
            </p>
          </Section>

          <Section title="12. Contact us">
            <p>
              Aspire Collective Partners LLC<br />
              Email: <a href="mailto:carlos@aspirecollectivepartnersllc.com" className="text-money font-semibold">carlos@aspirecollectivepartnersllc.com</a><br />
              Phone: <a href="tel:+16149664283" className="text-money font-semibold">(614) 966-4283</a>
            </p>
          </Section>
        </article>

        <div className="mt-14 text-center">
          <Link to="/" className="text-money font-semibold" data-testid="privacy-home-link">← Back to home</Link>
        </div>
      </div>
    </section>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-display text-2xl text-navy mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
